import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, ClipboardList } from 'lucide-react';
import { API } from '@/lib/api';

const TIEMPOS = [
  { key: 'desayuno',    label: 'Desayuno',     emoji: '🌅' },
  { key: 'colacion_am', label: 'Colación AM',  emoji: '🍎' },
  { key: 'comida',      label: 'Comida',       emoji: '🍽️' },
  { key: 'colacion_pm', label: 'Colación PM',  emoji: '🫐' },
  { key: 'cena',        label: 'Cena',         emoji: '🌙' },
];

const emptyForm = {
  fecha: new Date().toISOString().split('T')[0],
  desayuno: '', colacion_am: '', comida: '', colacion_pm: '', cena: '',
  agua_litros: '', observaciones: '',
};

export const Recordatorio24h = ({ patient }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState(null);

  const fetch = async () => {
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/recordatorios`);
      setRecords(res.data);
    } catch { toast.error('Error al cargar recordatorios'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [patient.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fecha) { toast.error('La fecha es obligatoria'); return; }
    const payload = {
      paciente_id: patient.id,
      ...form,
      agua_litros: form.agua_litros !== '' ? parseFloat(form.agua_litros) : null,
      desayuno: form.desayuno || null, colacion_am: form.colacion_am || null,
      comida: form.comida || null, colacion_pm: form.colacion_pm || null,
      cena: form.cena || null, observaciones: form.observaciones || null,
    };
    try {
      await axios.post(`${API}/patients/${patient.id}/recordatorios`, payload);
      toast.success('Recordatorio guardado');
      setDialogOpen(false);
      setForm(emptyForm);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este recordatorio? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/patients/${patient.id}/recordatorios/${id}`);
      setRecords(prev => prev.filter(r => r.id !== id));
      toast.success('Recordatorio eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setForm(emptyForm); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" />
          Nuevo recordatorio
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Cargando...</p>
      ) : records.length === 0 ? (
        <Card className="bg-card border-border/50">
          <CardContent className="flex flex-col items-center py-14 text-muted-foreground">
            <ClipboardList className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Sin recordatorios registrados</p>
            <p className="text-xs mt-1">Documenta qué come el paciente en 24 horas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <Card key={r.id} className="bg-card border-border/50 overflow-hidden">
              <button
                type="button"
                className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{r.fecha}</span>
                  {r.agua_litros && (
                    <span className="text-xs text-muted-foreground">💧 {r.agua_litros}L agua</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {[r.desayuno, r.colacion_am, r.comida, r.colacion_pm, r.cena].filter(Boolean).length}/5 tiempos
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive"
                    onClick={e => { e.stopPropagation(); handleDelete(r.id); }} aria-label="Eliminar recordatorio">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <span className="text-muted-foreground text-xs">{expanded === r.id ? '▲' : '▼'}</span>
                </div>
              </button>

              {expanded === r.id && (
                <div className="px-4 pb-4 space-y-2 border-t border-border/40">
                  {TIEMPOS.map(t => r[t.key] && (
                    <div key={t.key} className="flex gap-3 pt-2">
                      <span className="text-base w-6 shrink-0">{t.emoji}</span>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">{t.label}</p>
                        <p className="text-sm mt-0.5">{r[t.key]}</p>
                      </div>
                    </div>
                  ))}
                  {r.observaciones && (
                    <div className="pt-2 border-t border-border/40">
                      <p className="text-xs text-muted-foreground font-medium">Observaciones</p>
                      <p className="text-sm mt-0.5">{r.observaciones}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recordatorio de 24 horas</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Fecha *</Label>
              <Input type="date" value={form.fecha}
                onChange={e => setForm({ ...form, fecha: e.target.value })} />
            </div>
            {TIEMPOS.map(t => (
              <div key={t.key} className="space-y-1">
                <Label>{t.emoji} {t.label}</Label>
                <Textarea rows={2} placeholder={`¿Qué comió en el ${t.label.toLowerCase()}?`}
                  value={form[t.key]}
                  onChange={e => setForm({ ...form, [t.key]: e.target.value })} />
              </div>
            ))}
            <div className="space-y-1">
              <Label>💧 Agua (litros)</Label>
              <Input type="number" step="0.1" min="0" placeholder="1.5"
                value={form.agua_litros}
                onChange={e => setForm({ ...form, agua_litros: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Observaciones</Label>
              <Textarea rows={2} placeholder="Notas adicionales..."
                value={form.observaciones}
                onChange={e => setForm({ ...form, observaciones: e.target.value })} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="flex-1">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
